import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { api } from '../api.ts';
import type { Tracking } from '../lib/types.ts';
import { trackPathFor, unpaidOrder, useUnpaidOrder } from '../lib/unpaid.ts';

const UNPAID = new Set(['DRAFT', 'AWAITING_PAYMENT', 'PAYMENT_FAILED']);

/**
 * A slim bar on every page while this browser has an order that still needs paying, like the
 * banner iOS shows for a call in progress: tap it and you are back on the payment sheet.
 */
export function UnpaidBanner() {
  const pending = useUnpaidOrder();
  const { pathname } = useLocation();
  const onItsPage = pending ? pathname.startsWith(trackPathFor(pending.token)) : false;
  const q = useQuery({
    queryKey: ['track', pending?.token],
    queryFn: async () => (await api.get<Tracking>(`/track/${pending!.token}`)).data,
    enabled: Boolean(pending) && !onItsPage,
    refetchInterval: 30_000,
  });
  const status = q.data?.status;
  useEffect(() => {
    if (pending && status && !UNPAID.has(status)) unpaidOrder.forget(pending.orderId);
  }, [pending, status]);
  if (!pending || onItsPage || !status || !UNPAID.has(status)) return null;
  const failed = status === 'PAYMENT_FAILED';
  return (
    <Link
      to={trackPathFor(pending.token)}
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 52,
        padding: '8px 12px 8px 16px',
        margin: '0 0 12px',
        borderRadius: 16,
        background: 'var(--crave-red)',
        color: '#fff',
        textDecoration: 'none',
        boxShadow: '0 8px 24px rgba(200, 27, 34, 0.25)',
      }}
    >
      <span style={{ flex: 1, lineHeight: 1.3 }}>
        <strong>{failed ? "Payment didn't go through" : 'Your order isn’t paid yet'}</strong>
        <br />
        <span style={{ fontSize: 14, opacity: 0.9 }}>
          Order {q.data?.order_number ?? pending.orderNumber}
          {q.data?.hold_label ? ` · pickup held until ${q.data.hold_label}` : ''}
        </span>
      </span>
      <span
        style={{
          flex: 'none',
          padding: '8px 14px',
          borderRadius: 999,
          background: '#fff',
          color: 'var(--crave-red)',
          fontWeight: 700,
          fontSize: 15,
        }}
      >
        {failed ? 'Try Again' : 'Finish Payment'}
      </span>
    </Link>
  );
}
